/**
 * Default error messages (Spanish).
 *
 * This module is the seam for future i18n. When the app adopts a real i18n
 * framework (react-i18next, formatjs, etc.), swap the implementation of
 * `formatErrorMessage` to look up the active locale; the public surface
 * stays the same.
 *
 * Card 1AkpJZp1 — useApiError + toast central
 */

import type { ApiError, ApiErrorKind } from '@/api/errors';

/**
 * Default ES-AR/ES-LatAm copy for each normalized error kind.
 * Keep tone neutral, action-oriented, and short (toast UI).
 */
export const DEFAULT_ERROR_MESSAGES: Record<ApiErrorKind, string> = {
  unauthorized: 'Tu sesión expiró. Iniciá sesión nuevamente.',
  forbidden: 'No tenés permiso para realizar esta acción.',
  not_found: 'No encontramos lo que buscabas.',
  conflict: 'Esa acción no se puede completar en este momento.',
  validation: 'Los datos enviados no son válidos.',
  rate_limited: 'Demasiadas solicitudes. Probá de nuevo en unos segundos.',
  server: 'Tuvimos un problema en el servidor. Intentá de nuevo.',
  timeout: 'La solicitud tardó demasiado. Intentá de nuevo.',
  network: 'Sin conexión con el servidor. Revisá tu internet.',
  unknown: 'Ocurrió un error inesperado.',
};

/**
 * Pick the best message for a classified error.
 *
 * Priority:
 *  1. Backend-provided message (already stored in `err.message` by classifier
 *     when present).
 *  2. Default i18n copy for the `kind`.
 *
 * For `rate_limited` we append the retry hint when known.
 */
export function formatErrorMessage(err: ApiError): string {
  const base = err.message || DEFAULT_ERROR_MESSAGES[err.kind];
  if (err.kind === 'rate_limited' && typeof err.retryAfterSeconds === 'number') {
    return `${base} (en ${err.retryAfterSeconds}s)`;
  }
  return base;
}

/**
 * Copy for a chat-stream wire failure (card 2OaQ3yFr).
 *
 * Deliberately NOT a member of `DEFAULT_ERROR_MESSAGES`: this is not a new
 * `ApiErrorKind`, it is a `server` error whose raw message happens to be a
 * validator dump. The user gets prose; the dump stays in `cause` for Sentry.
 */
export const STREAM_CONTRACT_ERROR_MESSAGE =
  'Tuvimos un problema al recibir la respuesta. Intentá de nuevo.';

/**
 * Short label for the retry CTA button rendered inside the toast.
 */
export const RETRY_CTA_LABEL = 'Reintentar';

/**
 * Short label for the reauth CTA shown on 401.
 */
export const REAUTH_CTA_LABEL = 'Iniciar sesión';
